import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { navHeaders } from "../utils/static";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCartShopping,
  faHeart,
  faUser,
  faSignOut,
  faUserCircle,
  faBars,
  faXmark,
  faChevronDown,
  faHome,
  faBoxOpen,
  faInfoCircle,
  faPhoneAlt,
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import { useCartStore } from "../store/slice/cartSlice";
import { useAuthStore } from "../store/slice/authSlice";
import { toast } from "react-toastify";
  import LOGO from "../assets/Logo.png";
import { useWishlistStore } from "../store/slice/wishlistSlice";

function Header() {
  const { cart } = useCartStore();
  const { wishlist } = useWishlistStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [authModal, setAuthModal] = useState(false);
  const { isLoggedIn, userInfo, logout } = useAuthStore();
  const navigate = useNavigate();
  const modalRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setAuthModal(false);
      }
      
      // Don't close mobile menu when clicking inside it
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target) && 
          !event.target.closest('button[aria-label="Toggle menu"]')) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  
  // Prevent scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [mobileMenuOpen]);

  // Handle logout
  const handleLogout = () => {
    logout();
    setAuthModal(false);
    setMobileMenuOpen(false);
    navigate("/login");
    toast.success("Logged out successfully");
  };


  return (
    <header className="bg-white shadow-md sticky top-0 z-50">
      {/* Desktop & Tablet Navigation */}
      <div className="container mx-auto px-4 py-2 flex justify-between items-center relative">
        {/* Logo */}
        <Link to="/" className="flex items-center z-20">
          <img 
            src={LOGO} 
            alt="Grow Textile" 
            className="h-12 sm:h-14 md:h-16 w-auto" 
          />
        </Link>

        {/* Desktop Menu - hidden on mobile */}
        <nav className="hidden md:flex items-center space-x-1 lg:space-x-6">
          {navHeaders?.map((navHeader) => {
            const { id, name, path } = navHeader;
            const isActive = window.location.pathname === path;
            return (
              <Link
                key={id}
                to={path}
                className={`relative px-3 py-2 text-sm lg:text-base font-medium transition-all duration-200 ${
                  isActive 
                    ? "text-blue-600" 
                    : "text-gray-700 hover:text-blue-600"
                }`}
              >
                <span className="relative z-10">{name}</span>
                {isActive && (
                  <span className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 rounded-full" />
                )}
              </Link>
            );
          })}
          {isLoggedIn && userInfo?.role === "manufacturer" && (
            <Link
              to="/dashboard"
              className="ml-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors hidden md:block"
            >
              <FontAwesomeIcon icon={faBoxOpen} className="mr-2" />
              Dashboard
            </Link>
          )}
        </nav>

        {/* User Actions - updated for better tablet access */}
        <div className="flex items-center gap-2 sm:gap-4 z-20">
          {/* User Profile/Auth */}
          <div className="relative" ref={modalRef}>
            <button
              className="p-2 rounded-full text-gray-700 hover:bg-gray-100 hover:text-blue-600 
                transition-all duration-200 flex items-center gap-1"
              onClick={() => setAuthModal(!authModal)}
            >
              <FontAwesomeIcon icon={faUser} />
              {isLoggedIn && (
                <span className="text-sm hidden sm:block max-w-[90px] truncate">
                  {userInfo?.userName || "User"}
                </span>
              )}
            </button>
            {authModal && (
              <AuthModal
                setAuthModal={setAuthModal}
                isLoggedIn={isLoggedIn}
                userInfo={userInfo}
                onLogout={handleLogout}
              />
            )}
          </div>
          
          {/* Cart - show only if logged in */}
          {isLoggedIn && (
            <Link to="/cart" className="relative">
              {cart?.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-blue-600 text-white w-4 h-4 
                  flex items-center justify-center rounded-full text-xs animate-pulse">
                  {cart?.length}
                </span>
              )}
              <button className="p-2 rounded-full text-gray-700 hover:bg-gray-100 hover:text-blue-600 
                transition-all duration-200">
                <FontAwesomeIcon icon={faCartShopping} />
              </button>
            </Link>
          )}
          
          {/* Wishlist - show only if logged in, hide on small mobile */}
          {isLoggedIn && (
            <Link to="/wishlist" className="relative">
              {wishlist?.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-blue-600 text-white w-4 h-4 
                  flex items-center justify-center rounded-full text-xs animate-pulse">
                  {wishlist?.length}
                </span>
              )}
              <button className="p-2 rounded-full text-gray-700 hover:bg-gray-100 hover:text-blue-600 
                transition-all duration-200">
                <FontAwesomeIcon icon={faHeart} />
              </button>
            </Link>
          )}

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="ml-1 md:hidden p-2 rounded-full text-gray-700 hover:bg-gray-100 
              focus:outline-none transition-all duration-200"
            aria-label="Toggle menu"
          >
            <FontAwesomeIcon 
              icon={mobileMenuOpen ? faXmark : faBars} 
              className="text-xl" 
            />
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div 
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 md:hidden ${
          mobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Mobile Menu Panel - enhanced for better visibility */}
      <div 
        ref={mobileMenuRef}
        className={`fixed top-0 right-0 bottom-0 w-[80%] max-w-sm bg-white z-50 
          transform transition-transform duration-300 ease-in-out shadow-xl md:hidden
          ${mobileMenuOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="flex flex-col h-full overflow-y-auto">
          {/* Mobile Header with Quick Actions */}
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <img src={LOGO} alt="Grow Textile" className="h-10 w-auto" />
            
            {/* Quick action buttons in mobile header */}
            <div className="flex items-center gap-2">
              {isLoggedIn && userInfo?.role === "manufacturer" && (
                <Link
                  to="/dashboard"
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 rounded-full text-gray-700 hover:bg-gray-100"
                  title="Dashboard"
                >
                  <FontAwesomeIcon icon={faBoxOpen} />
                </Link>
              )}
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 rounded-full hover:bg-gray-100 text-gray-500"
              >
                <FontAwesomeIcon icon={faXmark} className="text-xl" />
              </button>
            </div>
          </div>

          {/* User Info with Quick Logout - highlight logout more */}
          {isLoggedIn && (
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                  <FontAwesomeIcon icon={faUser} />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-800">{userInfo?.userName}</div>
                  <div className="text-xs text-gray-500 truncate max-w-[200px]">{userInfo?.emailId}</div>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2 rounded-full text-red-600 hover:bg-red-50 transition-colors"
                  title="Logout"
                >
                  <FontAwesomeIcon icon={faSignOut} />
                </button>
              </div>
            </div>
          )}

          {/* Navigation Links */}
          <nav className="py-2">
            <ul className="space-y-1">
              {navHeaders?.map((navHeader) => {
                const { id, name, path } = navHeader;
                const isActive = window.location.pathname === path;
                
                // Add icons based on path
                let icon;
                if (path === '/') icon = faHome;
                else if (path === '/products') icon = faBoxOpen;
                else if (path === '/about') icon = faInfoCircle;
                else if (path === '/contact') icon = faPhoneAlt;
                else icon = faChevronDown;
                
                return (
                  <li key={id}>
                    <Link
                      to={path}
                      className={`flex items-center gap-3 px-4 py-3 ${
                        isActive 
                          ? "bg-blue-50 text-blue-600" 
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <FontAwesomeIcon icon={icon} className="w-5" fixedWidth />
                      <span>{name}</span>
                      
                      {isActive && (
                        <span className="w-1 h-6 bg-blue-600 rounded-full ml-auto" />
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Add a special Dashboard Section if user is manufacturer */}
          {isLoggedIn && userInfo?.role === "manufacturer" && (
            <div className="mt-4 mx-4">
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                <h3 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                  <FontAwesomeIcon icon={faBoxOpen} />
                  Manufacturer Dashboard
                </h3>
                <p className="text-sm text-blue-700 mb-3">
                  Manage your products, orders, and manufacturer settings.
                </p>
                <Link
                  to="/dashboard"
                  className="block w-full py-2 bg-blue-600 hover:bg-blue-700 text-white text-center rounded-md transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Go to Dashboard
                </Link>
              </div>
            </div>
          )}

          {/* Mobile User Actions - keep the existing bottom links for redundancy */}
          {isLoggedIn ? (
            <div className="mt-auto border-t">
              <Link
                to="/profile"
                className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-gray-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                <FontAwesomeIcon icon={faUserCircle} className="w-5" fixedWidth />
                <span>Profile</span>
              </Link>
              
              {/* Show wishlist in mobile menu */}
              <Link
                to="/wishlist"
                className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-gray-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                <FontAwesomeIcon icon={faHeart} className="w-5" fixedWidth />
                <span>Wishlist</span>
              </Link>
              
              {userInfo?.role === "manufacturer" && (
                <Link
                  to="/dashboard"
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-gray-700"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <FontAwesomeIcon icon={faBoxOpen} className="w-5" fixedWidth />
                  <span>Dashboard</span>
                </Link>
              )}
              
              {/* Make logout more prominent */}
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-4 py-3 bg-red-50 hover:bg-red-100 text-red-600 w-full text-left font-medium"
              >
                <FontAwesomeIcon icon={faSignOut} className="w-5" fixedWidth />
                <span>Logout</span>
              </button>
            </div>
          ) : (
            <div className="mt-auto p-4 border-t flex flex-col gap-2">
              <Link
                to="/login"
                className="w-full py-2 bg-blue-600 text-white text-center rounded-md hover:bg-blue-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                Login
              </Link>
              <Link
                to="/register"
                className="w-full py-2 border border-gray-300 text-gray-700 text-center rounded-md hover:bg-gray-50"
                onClick={() => setMobileMenuOpen(false)}
              >
                Register
              </Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

const AuthModal = ({ setAuthModal, isLoggedIn, userInfo, onLogout }) => {
  return (
    <div className="absolute right-0 top-12 bg-white rounded-lg shadow-lg w-56 py-2 z-50 
      animate-fadeIn border border-gray-100">
      {isLoggedIn ? (
        <div className="flex flex-col">
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="font-medium text-gray-800">{userInfo?.userName}</div>
            <div className="text-xs text-gray-500 truncate">{userInfo?.emailId}</div>
          </div>
          <Link
            to="/profile"
            className="px-4 py-2 hover:bg-gray-100 text-gray-800 flex items-center gap-2"
            onClick={() => setAuthModal(false)}
          >
            <FontAwesomeIcon icon={faUserCircle} />
            <span>Profile</span>
          </Link>
          <button
            onClick={onLogout}
            className="px-4 py-2 hover:bg-gray-100 text-red-600 flex items-center gap-2 w-full text-left"
          >
            <FontAwesomeIcon icon={faSignOut} />
            <span>Logout</span>
          </button>
        </div>
      ) : (
        <div className="flex flex-col p-2">
          <Link
            to="/login"
            className="px-4 py-2 bg-blue-600 text-white text-center rounded-md hover:bg-blue-700 mb-2"
            onClick={() => setAuthModal(false)}
          >
            Login
          </Link>
          <Link
            to="/register"
            className="px-4 py-2 border border-gray-300 text-gray-700 text-center rounded-md hover:bg-gray-50"
            onClick={() => setAuthModal(false)}
          >
            Register
          </Link>
        </div>
      )}
    </div>
  );
};

export default Header;
